// Shopify owns consent/delivery. Only empty, allowlisted funnel signals are published.
(() => {
  if (window.KiddoWonderFunnel) return;
  const names = new Set(['book_selected', 'preview_started', 'preview_generated', 'personalized_preview_viewed']);
  const seen = new Set();
  const watchers = new WeakMap();
  const emit = (name, key) => {
    try {
      if (!names.has(name)) return;
      const id = key ? name + ':' + key : null;
      if (id && seen.has(id)) return;
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish !== 'function') return;
      if (id) {
        seen.add(id);
        if (seen.size > 1000) seen.delete(seen.values().next().value);
      }
      Promise.resolve(analytics.publish('kiddowonder:' + name, {})).catch(() => {});
    } catch (_) { /* Measurement cannot interrupt a preview. */ }
  };
  const viewed = (image, key) => {
    try {
      if (!image || !key || !window.IntersectionObserver) return;
      watchers.get(image)?.();
      let visible = false;
      const cleanup = () => {
        observer.disconnect();
        image.removeEventListener('load', check);
        document.removeEventListener('visibilitychange', check);
      };
      const check = () => {
        if (visible && document.visibilityState === 'visible' && image.complete && image.naturalWidth > 0 && !image.hidden) {
          emit('personalized_preview_viewed', key);
          cleanup();
        }
      };
      const observer = new window.IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25);
        check();
      }, { threshold: 0.25 });
      watchers.set(image, cleanup);
      image.addEventListener('load', check);
      document.addEventListener('visibilitychange', check);
      observer.observe(image);
    } catch (_) { /* Unsupported browser or observer must not affect the UI. */ }
  };
  window.KiddoWonderFunnel = { emit, viewed };
})();

(function () {
  const STORY_SUMMARIES = {
    'brothers-secret-fort': 'A blanket fort becomes a magical kingdom where two brothers learn that the strongest plans make room for both of their ideas.',
    'sister-brother-lantern-bridge': 'A sister and brother combine two different clues to relight a magical bridge, building trust one step at a time.',
    'sisters-moonlight-garden': 'Two sisters bring a moonlit garden to life by celebrating the different gifts each one brings.',
    'mother-child-memory-recipe': 'A mother and child collect laughter, patience, helpful hands, and shared love to create a recipe worth remembering.',
    'mother-daughter-starlight-quilt': 'A mother and daughter stitch courage, kindness, and favorite memories into a quilt that glows with love.',
    'father-son-workshop-of-wonders': 'A father and son repair a tiny town’s morning machine through curiosity, careful testing, and trying again.',
    'father-daughter-kite-beyond-the-hill': 'A runaway kite leads a father and daughter beyond the familiar hill, where courage grows one careful step at a time.',
    'grandfather-granddaughter-map-of-little-treasures': 'A grandfather and granddaughter follow a glowing map to discover family stories, kindness, and time together.',
    'grandfather-grandson-clockmakers-compass': 'A grandfather and grandson restore a village’s missing Slow Minute by combining old wisdom with new ideas.',
    'grandmother-granddaughter-garden-of-wishes': 'A grandmother and granddaughter discover that wishes grow when they are cared for with patience and love.',
    'grandmother-grandson-storybook-key': 'A grandmother and grandson unlock an unfinished tale where memory and imagination must write the ending together.'
  };

  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  class TogetherTaleBuilder {
    constructor(root) {
      this.root = root;
      const requestedStory = (new URL(window.location.href).searchParams.get('book') || '').trim().toLowerCase();
      if (!STORY_SUMMARIES[requestedStory]) return;
      root.hidden = false;
      this.engine = String(root.dataset.engineUrl || '').replace(/\/+$/, '');
      this.hero = root.querySelector('.kw-together__hero');
      this.form = root.querySelector('[data-together-form]');
      this.alert = root.querySelector('[data-together-alert]');
      this.loading = root.querySelector('[data-together-loading]');
      this.result = root.querySelector('[data-together-result]');
      this.story = root.querySelector('[data-together-story]');
      this.summary = root.querySelector('[data-story-summary]');
      this.photoModeInputs = Array.from(root.querySelectorAll('[data-photo-mode]'));
      this.togetherPhotoCard = root.querySelector('[data-together-photo-card]');
      this.separatePhotoFields = Array.from(root.querySelectorAll('[data-separate-photo]'));
      this.purchaseButtons = Array.from(root.querySelectorAll('[data-purchase-button]'));
      this.purchaseStatus = root.querySelector('[data-purchase-status]');
      this.purchaseConfiguration = root.querySelector('[data-purchase-configuration]');
      this.batch = '';
      this.preview = null;
      this.product = null;
      this.productLoading = false;
      this.productError = '';
      this.productPromise = Promise.resolve(null);
      this.clientId = '';
      this.bind();
      this.selectRequestedStory();
      if (requestedStory && this.story.value === requestedStory) window.KiddoWonderFunnel?.emit('book_selected');
      this.updateStory();
    }

    bind() {
      this.story.addEventListener('change', () => {
        this.updateStory();
        window.KiddoWonderFunnel?.emit('book_selected');
      });
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.root.querySelectorAll('[data-photo-input]').forEach((input) => {
        input.addEventListener('change', () => this.previewPhoto(input));
      });
      this.photoModeInputs.forEach((input) => {
        input.addEventListener('change', () => this.updatePhotoMode());
      });
      this.root.querySelector('[data-generate-pages]').addEventListener('click', () => this.generatePages());
      this.root.querySelector('[data-regenerate-cover]').addEventListener('click', () => this.regenerate());
      this.root.querySelector('[data-edit-details]').addEventListener('click', () => this.editDetails());
      this.root.querySelector('[data-start-over]').addEventListener('click', () => window.location.reload());
      this.purchaseButtons.forEach((button) => {
        button.addEventListener('click', () => this.addToCart(button.dataset.coverType, button));
      });
    }

    selectRequestedStory() {
      const requested = new URL(window.location.href).searchParams.get('book');
      if (requested && this.story.querySelector(`option[value="${CSS.escape(requested)}"]`)) this.story.value = requested;
    }

    updateStory() {
      const option = this.story.selectedOptions[0];
      const roleOne = option.dataset.roleOne || 'Person one';
      const roleTwo = option.dataset.roleTwo || 'Person two';
      this.root.querySelector('[data-person-heading="1"]').textContent = roleOne;
      this.root.querySelector('[data-person-heading="2"]').textContent = roleTwo;
      this.root.querySelector('[data-name-label="1"]').textContent = `${roleOne}’s name`;
      this.root.querySelector('[data-name-label="2"]').textContent = `${roleTwo}’s name`;
      this.summary.textContent = STORY_SUMMARIES[this.story.value] || '';
      this.product = null;
      this.productError = '';
      this.productPromise = this.loadProduct();
      this.renderPurchase();
      this.updatePhotoMode();
    }

    photoMode() {
      const selected = this.photoModeInputs.find((input) => input.checked);
      return selected && selected.value === 'together' ? 'together' : 'separate';
    }

    updatePhotoMode() {
      const together = this.photoMode() === 'together';
      this.togetherPhotoCard.hidden = !together;
      const togetherInput = this.form.elements.reference_together;
      if (togetherInput) togetherInput.required = together;
      this.separatePhotoFields.forEach((field) => {
        field.hidden = together;
        const input = field.querySelector('input[type="file"]');
        if (input) input.required = !together;
      });
      this.root.querySelectorAll('[data-person-help]').forEach((help) => {
        help.textContent = together
          ? 'Add their name and age so we can identify them correctly in the shared photo.'
          : 'Use one clear, front-facing photo of this person only.';
      });
      this.root.querySelectorAll('[data-person-step]').forEach((step, index) => {
        step.textContent = String(index + (together ? 4 : 3));
      });
      const privacyTitle = this.root.querySelector('[data-photo-privacy-title]');
      const privacyCopy = this.root.querySelector('[data-photo-privacy-copy]');
      if (privacyTitle) privacyTitle.textContent = 'How we use these photos and details.';
      if (privacyCopy) {
        const photoGuidance = together
          ? 'Use a shared photo only when both people are visible and you have permission to upload it. '
          : 'Use separate photos only when you have permission to upload each one. ';
        privacyCopy.textContent = `${photoGuidance}We use photos, names, and ages to generate the preview, produce and fulfil the personalized book, prevent misuse, and provide order support. They are not made public or used in marketing without separate permission. See the full privacy policy for providers, retention, and your choices.`;
      }
      this.clearError();
    }

    previewPhoto(input) {
      const number = input.dataset.photoInput;
      const file = input.files && input.files[0];
      const image = this.root.querySelector(`[data-photo-preview="${number}"]`);
      const empty = this.root.querySelector(`[data-upload-empty="${number}"]`);
      const ready = this.root.querySelector(`[data-photo-ready="${number}"]`);
      if (!file) {
        image.removeAttribute('src');
        image.hidden = true;
        empty.hidden = false;
        ready.hidden = true;
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        input.value = '';
        this.showError('Each photo must be 15 MB or smaller.');
        return;
      }
      image.src = URL.createObjectURL(file);
      image.hidden = false;
      empty.hidden = true;
      ready.hidden = false;
    }

    showError(message) {
      this.alert.textContent = message;
      this.alert.hidden = false;
      this.alert.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    clearError() {
      this.alert.hidden = true;
      this.alert.textContent = '';
    }

    apiUrl(path) {
      if (/^https?:\/\//.test(path)) return path;
      return `${this.engine}${path.startsWith('/') ? path : `/${path}`}`;
    }

    async json(url, options) {
      const response = await fetch(this.apiUrl(url), options);
      let data = {};
      try { data = await response.json(); } catch (error) { /* handled below */ }
      if (!response.ok) throw new Error(data.error || 'The preview service could not complete that request.');
      return data;
    }

    validate() {
      if (this.photoMode() === 'together') {
        if (!this.form.elements.reference_together.files[0]) {
          throw new Error('Please add one clear photo showing both people.');
        }
      } else {
        const one = this.form.elements.reference_one.files[0];
        const two = this.form.elements.reference_two.files[0];
        if (!one || !two) throw new Error('Please add one separate photo for each person.');
        if (one.name === two.name && one.size === two.size && one.lastModified === two.lastModified) {
          throw new Error('Please choose two different photos—one clear photo for each person.');
        }
      }
      if (!this.form.elements.name_one.value.trim() || !this.form.elements.name_two.value.trim()) throw new Error('Please add both names.');
      if (this.form.elements.name_one.value.trim().toLowerCase() === this.form.elements.name_two.value.trim().toLowerCase()) throw new Error('Please use a different name for each person so their roles stay clear.');
    }

    async submit(event) {
      event.preventDefault();
      this.clearError();
      try { this.validate(); } catch (error) { this.showError(error.message); return; }
      const submit = this.root.querySelector('[data-together-submit]');
      submit.disabled = true;
      this.root.setAttribute('aria-busy', 'true');
      this.hero.hidden = true;
      this.form.hidden = true;
      this.result.hidden = true;
      this.loading.hidden = false;
      this.root.querySelector('[data-loading-title]').textContent = 'Creating both characters…';
      this.root.querySelector('[data-loading-stage]').textContent = this.photoMode() === 'together'
        ? 'Finding and matching both people in the shared photo.'
        : 'Keeping each face matched to the correct person.';
      this.loading.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        window.KiddoWonderFunnel?.emit('preview_started');
        const data = await this.json('/api/together-tale/generate', { method: 'POST', body: new FormData(this.form) });
        this.batch = data.batch;
        await this.poll(false);
      } catch (error) {
        this.loading.hidden = true;
        this.hero.hidden = false;
        this.form.hidden = false;
        submit.disabled = false;
        this.showError(error.message);
      } finally {
        this.root.setAttribute('aria-busy', 'false');
      }
    }

    async poll(waitingForPages) {
      const started = Date.now();
      while (Date.now() - started < 12 * 60 * 1000) {
        const status = await this.json(`/api/together-tale/${this.batch}`);
        this.root.querySelector('[data-loading-stage]').textContent = status.stage || (waitingForPages ? 'Painting the opening pages…' : 'Creating your cover…');
        this.root.querySelector('[data-loading-bar]').style.width = `${Math.max(6, Number(status.progress) || 0)}%`;
        if (status.status === 'error') throw new Error(status.error || 'We could not finish this preview. Please try again.');
        if ((!waitingForPages && status.cover_ready) || (waitingForPages && status.ready)) {
          window.KiddoWonderFunnel?.emit('preview_generated', this.batch);
          await this.loadPreview();
          return;
        }
        await wait(1800);
      }
      throw new Error('This preview is taking longer than expected. Please try again.');
    }

    async loadPreview() {
      this.preview = await this.json(`/api/together-tale/${this.batch}/preview`);
      this.loading.hidden = true;
      this.result.hidden = false;
      this.root.querySelector('[data-result-title]').textContent = 'Your personalized cover is ready!';
      this.updateResultStatus();
      const option = this.story.selectedOptions[0];
      const personOne = [
        this.form.elements.name_one.value.trim(),
        this.form.elements.age_one.value.trim() ? `age ${this.form.elements.age_one.value.trim()}` : '',
        option.dataset.roleOne || ''
      ].filter(Boolean).join(' · ');
      const personTwo = [
        this.form.elements.name_two.value.trim(),
        this.form.elements.age_two.value.trim() ? `age ${this.form.elements.age_two.value.trim()}` : '',
        option.dataset.roleTwo || ''
      ].filter(Boolean).join(' · ');
      this.root.querySelector('[data-result-meta]').textContent = `${personOne}  |  ${personTwo}`;
      const cover = this.root.querySelector('[data-result-cover]');
      cover.src = this.apiUrl(this.preview.cover && this.preview.cover.image_url || '');
      if (this.preview.cover?.image_url) window.KiddoWonderFunnel?.viewed(cover, this.batch);
      this.renderPages();
      this.renderPurchase();
      this.result.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderPages() {
      const wrap = this.root.querySelector('[data-preview-pages]');
      const pages = this.preview && Array.isArray(this.preview.pages) ? this.preview.pages : [];
      const ready = pages.filter((page) => page.ready && page.image_url);
      const requested = Boolean(this.preview && this.preview.pages_requested) || ready.length > 0;
      for (let pageNumber = 1; pageNumber <= 3; pageNumber += 1) {
        const page = pages.find((entry) => Number(entry.page) === pageNumber) || pages[pageNumber - 1] || {};
        const card = wrap.querySelector(`[data-page-card="${pageNumber}"]`);
        const image = wrap.querySelector(`[data-page-image="${pageNumber}"]`);
        const loading = wrap.querySelector(`[data-page-loading="${pageNumber}"]`);
        const copy = wrap.querySelector(`[data-page-copy="${pageNumber}"]`);
        const title = wrap.querySelector(`[data-page-title="${pageNumber}"]`);
        const text = wrap.querySelector(`[data-page-text="${pageNumber}"]`);
        const isReady = Boolean(page.ready && page.image_url);
        card.classList.toggle('is-ready', isReady);
        card.setAttribute('aria-busy', isReady ? 'false' : 'true');
        loading.hidden = isReady;
        image.hidden = !isReady;
        copy.hidden = !isReady;
        if (isReady) {
          const nextSource = this.apiUrl(page.image_url);
          if (image.src !== nextSource) image.src = nextSource;
          title.textContent = page.title || `Page ${pageNumber}`;
          text.textContent = page.text || '';
        }
      }
      wrap.hidden = !requested;
      const button = this.root.querySelector('[data-generate-pages]');
      button.hidden = requested;
      button.disabled = requested;
    }

    updateResultStatus(status) {
      const preview = this.preview || {};
      const ready = Number(status && status.pages_ready != null ? status.pages_ready : preview.pages_ready) || 0;
      const total = Number(status && status.pages_total != null ? status.pages_total : preview.pages_total) || 3;
      const pagesRequested = Boolean(status && status.pages_requested != null ? status.pages_requested : preview.pages_requested);
      const resultStatus = this.root.querySelector('[data-result-status]');
      if (ready >= total) resultStatus.textContent = 'Your cover and first pages are ready. You can still choose either book format below.';
      else if (pagesRequested) resultStatus.textContent = ready > 0
        ? `${ready} of ${total} opening pages are ready. The rest will appear here automatically.`
        : 'Your cover is ready. We’re painting the opening pages here now.';
      else resultStatus.textContent = 'Your cover is ready. See the first pages when you’re ready.';
    }

    async postWithRetry(path, attempts) {
      let lastError;
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try { return await this.json(path, { method: 'POST' }); }
        catch (error) { lastError = error; if (attempt < attempts) await wait(attempt * 900); }
      }
      throw lastError;
    }

    async generatePages() {
      const button = this.root.querySelector('[data-generate-pages]');
      const regenerateButton = this.root.querySelector('[data-regenerate-cover]');
      button.disabled = true;
      regenerateButton.disabled = true;
      this.root.setAttribute('aria-busy', 'true');
      this.clearError();
      this.preview.pages_requested = true;
      this.preview.pages_ready = Number(this.preview.pages_ready) || 0;
      this.renderPages();
      this.updateResultStatus();
      const pages = this.root.querySelector('[data-preview-pages]');
      pages.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      try {
        await this.postWithRetry(`/api/together-tale/${this.batch}/pages`, 3);
        await this.pollPagesInline();
      } catch (error) {
        this.preview.pages_requested = false;
        this.renderPages();
        this.updateResultStatus();
        button.disabled = false;
        button.hidden = false;
        button.textContent = 'Try the pages again';
        regenerateButton.disabled = false;
        this.root.setAttribute('aria-busy', 'false');
        this.showError(error.message);
      }
    }

    async pollPagesInline() {
      const started = Date.now();
      while (Date.now() - started < 12 * 60 * 1000) {
        const status = await this.json(`/api/together-tale/${this.batch}`);
        if (status.status === 'error') throw new Error(status.error || 'We could not finish these pages. Please try again.');
        this.preview = await this.json(`/api/together-tale/${this.batch}/preview`);
        this.renderPages();
        this.updateResultStatus(status);
        this.renderPurchase();
        if (status.ready) {
          this.root.querySelector('[data-regenerate-cover]').disabled = false;
          this.root.setAttribute('aria-busy', 'false');
          return;
        }
        await wait(1800);
      }
      throw new Error('These pages are taking longer than expected. Please try again.');
    }

    async regenerate() {
      const button = this.root.querySelector('[data-regenerate-cover]');
      button.disabled = true;
      this.clearError();
      this.result.hidden = true;
      this.loading.hidden = false;
      this.root.querySelector('[data-loading-title]').textContent = 'Creating another cover…';
      try {
        await this.postWithRetry(`/api/together-tale/${this.batch}/regenerate`, 2);
        await this.poll(false);
      } catch (error) {
        this.loading.hidden = true;
        this.result.hidden = false;
        this.showError(error.message);
      } finally { button.disabled = false; }
    }

    editDetails() {
      this.clearError();
      this.loading.hidden = true;
      this.result.hidden = true;
      this.hero.hidden = false;
      this.form.hidden = false;
      const submit = this.root.querySelector('[data-together-submit]');
      submit.disabled = false;
      this.root.setAttribute('aria-busy', 'false');
      this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async loadProduct() {
      const productSlug = this.story.value;
      this.productLoading = true;
      this.productError = '';
      try {
        const response = await fetch(this.shopifyRoute(`products/${encodeURIComponent(productSlug)}.js`), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        const product = await response.json().catch(() => ({}));
        if (!response.ok || !product || !product.id) {
          throw new Error(product.description || product.message || 'The story product could not be found.');
        }
        if (this.story.value === productSlug) this.product = product;
        return product;
      } catch (error) {
        if (this.story.value === productSlug) {
          this.product = null;
          this.productError = error && error.message ? error.message : 'The story product could not be loaded.';
        }
        return null;
      } finally {
        if (this.story.value === productSlug) {
          this.productLoading = false;
          this.renderPurchase();
        }
      }
    }

    money(cents) {
      const currency = window.Shopify && window.Shopify.currency && (window.Shopify.currency.active || window.Shopify.currency.current) || 'EUR';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(cents) / 100); }
      catch (error) { return `${(Number(cents) / 100).toFixed(2)} ${currency}`; }
    }

    renderPurchase() {
      const hasCover = Boolean(this.preview && this.preview.cover && this.preview.cover.image_url);
      const missing = [];
      this.purchaseButtons.forEach((button) => {
        const coverType = button.dataset.coverType;
        const variant = this.findCoverVariant(coverType);
        const available = Boolean(variant && variant.id && variant.available !== false);
        const action = button.querySelector('[data-purchase-action]');
        const price = button.querySelector('[data-purchase-price]');
        if (action) action.textContent = coverType === 'softcover' ? 'Get the Softcover Book' : 'Get the Hardcover Book';
        if (price && variant && variant.price != null) price.textContent = this.money(variant.price);
        button.dataset.variantId = available ? String(variant.id) : '';
        button.disabled = !hasCover || !available;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        if (!available) missing.push(coverType === 'softcover' ? 'Softcover' : 'Hardcover');
      });

      if (this.purchaseConfiguration) {
        const isConfigured = missing.length === 0;
        this.purchaseConfiguration.hidden = this.productLoading || isConfigured;
        if (!this.productLoading && !isConfigured) {
          this.purchaseConfiguration.textContent = this.productError
            ? `Store setup required: Shopify could not load the “${this.story.value}” product. ${this.productError}`
            : `Store setup required: add ${missing.join(' and ')} variants to this Together Tale product.`;
        }
      }
      if (!this.purchaseStatus) return;
      if (this.productLoading) this.purchaseStatus.textContent = 'Loading purchase options…';
      else if (missing.length) this.purchaseStatus.textContent = `${missing.join(' and ')} ${missing.length === 1 ? 'is' : 'are'} being configured.`;
      else if (hasCover) this.purchaseStatus.textContent = 'You will get the full ebook after you purchase so that you confirm every page and then we will print.';
      else this.purchaseStatus.textContent = 'Purchase options appear as soon as your cover is ready.';
    }

    findCoverVariant(coverType) {
      const variants = this.product && Array.isArray(this.product.variants) ? this.product.variants : [];
      return variants.find((variant) => {
        const label = [variant.title, variant.name, variant.option1, ...(variant.options || [])]
          .filter(Boolean).join(' ').toLowerCase();
        return label.includes(coverType);
      }) || null;
    }

    shopifyRoute(path) {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      return `${root.replace(/\/?$/, '/')}${String(path || '').replace(/^\//, '')}`;
    }

    getClientId() {
      if (this.clientId) return this.clientId;
      try {
        const existing = window.localStorage.getItem('kb_uid');
        if (/^[a-f0-9]{32}$/i.test(existing || '')) {
          this.clientId = existing;
          return existing;
        }
      } catch (error) { /* Continue with an in-memory ID. */ }
      const bytes = new Uint8Array(16);
      if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
      else bytes.forEach((value, index) => { bytes[index] = Math.floor(Math.random() * 256); });
      this.clientId = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
      try { window.localStorage.setItem('kb_uid', this.clientId); } catch (error) { /* Storage may be unavailable. */ }
      return this.clientId;
    }

    async ensureSingleTogetherTale(variantId) {
      const response = await fetch(this.shopifyRoute('cart.js'), {
        method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store'
      });
      const cart = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(cart.items)) {
        throw new Error(cart.description || cart.message || 'Shopify could not check your cart. Please try again.');
      }
      const alreadyAdded = cart.items.some((item) => {
        const properties = item.properties || {};
        return String(item.variant_id || item.id) === String(variantId)
          || item.handle === this.story.value
          || properties['_Together Tale Story'] === this.story.value
          || properties['_Book Slug'] === this.story.value;
      });
      if (alreadyAdded) throw new Error('This Together Tale is already in your cart. Please complete that order or remove it before adding another.');
    }

    async addToCart(coverType, button) {
      this.clearError();
      if (!this.batch || !(this.preview && this.preview.cover && this.preview.cover.image_url)) {
        this.showError('Please wait until your personalized cover is ready before adding the book to your cart.');
        return;
      }
      await this.productPromise;
      const variant = this.findCoverVariant(coverType);
      if (!variant || !variant.id || variant.available === false) {
        this.showError(`The ${coverType === 'softcover' ? 'Softcover' : 'Hardcover'} option is not available yet.`);
        return;
      }

      this.purchaseButtons.forEach((purchaseButton) => { purchaseButton.disabled = true; });
      const action = button.querySelector('[data-purchase-action]');
      if (action) action.textContent = 'Adding to cart…';
      try {
        const option = this.story.selectedOptions[0];
        const variantId = String(variant.id);
        await this.ensureSingleTogetherTale(variantId);
        const response = await fetch(this.shopifyRoute('cart/add.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: {
            'Person 1': this.form.elements.name_one.value.trim(),
            'Person 1 Age': this.form.elements.age_one.value.trim(),
            'Person 2': this.form.elements.name_two.value.trim(),
            'Person 2 Age': this.form.elements.age_two.value.trim(),
            'Relationship': `${option.dataset.roleOne} & ${option.dataset.roleTwo}`,
            'Photo Source': this.photoMode() === 'together' ? 'One photo together' : 'Two separate photos',
            'Cover Type': coverType === 'softcover' ? 'Softcover' : 'Hardcover',
            '_Together Tale batch': this.batch,
            '_Batch': this.batch,
            '_Book Slug': this.story.value,
            '_Together Tale Story': this.story.value,
            '_KB UID': this.getClientId(),
            '_Cover Image': `${this.apiUrl(`/api/custom-tale/${encodeURIComponent(this.batch)}/cover`)}?v=${Date.now()}`
          } }] })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'This book could not be added to the cart.');
        const added = Array.isArray(result.items) && result.items.some((item) => {
          const properties = item.properties || {};
          return String(item.variant_id || item.id) === variantId && properties['_Batch'] === this.batch;
        });
        if (!added) throw new Error('Shopify did not confirm the personalized book. Please try again.');
        window.location.assign(this.root.dataset.cartUrl || '/cart');
      } catch (error) {
        this.renderPurchase();
        this.showError(error.message);
      }
    }

    escape(value) {
      const div = document.createElement('div'); div.textContent = value; return div.innerHTML;
    }
  }

  document.querySelectorAll('[data-together-tale-builder]').forEach((root) => new TogetherTaleBuilder(root));
})();
